import { i18n, SUPPORTED_LOCALES, FALLBACK_LOCALE, type Locale } from './i18n'

const SUPPORTED = new Set<string>(SUPPORTED_LOCALES)

/**
 * 解析 URL 中的 l / lang 参数，并写入 i18n.global.locale。
 *
 * 按 BCP 47（RFC 5646）解析：
 * 1) 用 Intl.Locale 提取 primary language subtag（自带大小写归一）
 * 2) 命中 SUPPORTED_LOCALES 即采用
 * 3) 非法 / 非预设语言 → fallbackLocale
 *
 * 兼容输入：
 * - `zh` / `zh-CN` / `zh-TW` / `zh-HK` / `zh-Hant` → `zh`
 * - `en` / `en-US` / `en-GB` → `en`
 * - `zh_CN` 这种非标准下划线分隔 → 归一为 `-` 后再走 BCP 47
 *
 * URL 是 host 切换语言的唯一入口；其它代码应读 i18n.global.locale.value，
 * 而不是再次解析 URL。
 */
export function applyLocaleFromUrl(search?: string): Locale {
  const query = search ?? (typeof window !== 'undefined' ? window.location.search : '')

  const params = new URLSearchParams(query)
  const raw = (params.get('l') ?? params.get('lang') ?? '').trim()

  const resolved = resolveLocale(raw)

  i18n.global.locale.value = resolved
  return resolved
}

function resolveLocale(raw: string): Locale {
  if (!raw) return FALLBACK_LOCALE

  // 容忍下划线变体：zh_CN → zh-CN（BCP 47 规范分隔符是 `-`）
  const normalized = raw.replace(/_/g, '-')

  let language: string
  try {
    language = new Intl.Locale(normalized).language
  } catch {
    // 非合法 BCP 47 标签
    return FALLBACK_LOCALE
  }

  return SUPPORTED.has(language) ? (language as Locale) : FALLBACK_LOCALE
}
